import { useEffect, useState } from "react";
import ReactSelect from "react-select";
import "./complaint.css";
import {
  createComplaint,
  getAllUsers,
  utrVerify,
} from "../../../config/LiveApi";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const toDDMMYYYY = (date) => {
  if (!date) return "";

  // Excel numeric date
  if (typeof date === "number") {
    const d = new Date((date - 25569) * 86400 * 1000);
    return toDDMMYYYY(d);
  }

  // JS Date
  if (date instanceof Date && !isNaN(date)) {
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }

  // Already DD-MM-YYYY
  if (/^\d{2}-\d{2}-\d{4}$/.test(date)) return date;

  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [y, m, d] = date.split("-");
    return `${d}-${m}-${y}`;
  }

  return "";
};

const toYYYYMMDD = (ddmmyyyy) => {
  if (!ddmmyyyy) return "";
  const [dd, mm, yyyy] = ddmmyyyy.split("-");
  return `${yyyy}-${mm}-${dd}`;
};

const emptyUtr = {
  orderId: "",
  utr: "",
  merchantId: "",
  merchantName: "",
  customerName: "",
  complainterName: "",
  complainterPhone: "",
  complaintDate: "",
  email: "",
  phone: "",
  vpa: "",
  amount: "",
  fees: "",
};
function ComplaintForm() {
  const token = useSelector((store) => store.userReducer.token);
  const [merchantNames, setMerchantNames] = useState([]);
  const [merchantMap, setMerchantMap] = useState({});

  const [acks, setAcks] = useState([
    {
      ackNo: "",
      utrs: [{ ...emptyUtr }],
    },
  ]);

  useEffect(() => {
    const fetchMerchants = async () => {
      try {
        const res = await getAllUsers(token, 1);

        const admins = res.data.filter((u) => u.role === "admin");
        setMerchantNames(admins);

        const map = admins.reduce((acc, m) => {
          const key = (m.alias || m.id).toLowerCase().trim();
          acc[key] = {
            id: m.id,
            alias: m.alias || m.id,
          };
          return acc;
        }, {});

        setMerchantMap(map);
      } catch (e) {}
    };

    if (token) fetchMerchants();
  }, [token]);

  const MerchantOptions = merchantNames.map((m) => ({
    value: m.id,
    label: m.alias || m.id,
  }));

  const addAck = () => {
    setAcks([
      ...acks,
      {
        ackNo: "",
        utrs: [{ ...emptyUtr }],
      },
    ]);
  };

  const updateAckNo = (ackIndex, value) => {
    const updated = [...acks];
    updated[ackIndex].ackNo = value;
    setAcks(updated);
  };

  const addUtr = (ackIndex) => {
    const updated = [...acks];
    updated[ackIndex].utrs.push({ ...emptyUtr });
    setAcks(updated);
  };

  const removeUtr = (ackIndex, utrIndex) => {
    const updated = [...acks];
    updated[ackIndex].utrs.splice(utrIndex, 1);
    setAcks(updated);
  };

  const updateUtr = (ackIndex, utrIndex, field, value) => {
    const updated = [...acks];
    updated[ackIndex].utrs[utrIndex][field] = value;
    setAcks(updated);
  };

  const safeValue = (val) =>
    val === "null" || val === undefined ? "" : String(val).trim();

  const handleExcelUpload = (file) => {
    const REQUIRED_COLUMNS = [
      "ACKNOWLEDGMENT NO",
      "MERCHANT NAME",
      "UTR",
      "ORDERLD",
      "AMOUNT",
    ];

    const reader = new FileReader();
    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);

      if (!rows.length) {
        toast.error("Excel is empty");
        return;
      }

      const excelHeaders = Object.keys(rows[0] || {}).map((h) =>
        h.toUpperCase().trim(),
      );

      const missingColumns = REQUIRED_COLUMNS.filter(
        (col) => !excelHeaders.includes(col),
      );

      if (missingColumns.length) {
        toast.error(
          `Invalid Excel format. Missing columns: ${missingColumns.join(", ")}`,
        );
        return;
      }
      const grouped = {};

      rows.forEach((row) => {
        const ackNo = String(row["ACKNOWLEDGMENT NO"] || "").trim();
        if (!ackNo) return;
        const merchantFromExcel = String(row["MERCHANT NAME"] || "")
          .toLowerCase()
          .trim();
        const matchedMerchant = merchantMap[merchantFromExcel];

        if (!grouped[ackNo]) grouped[ackNo] = [];

        grouped[ackNo].push({
          orderId: safeValue(row["ORDERLD"]),
          utr: safeValue(row["UTR"]),
          merchantId: matchedMerchant?.id || "",
          merchantName: matchedMerchant?.alias || "",
          customerName: safeValue(row["PAYER"]),
          complainterName: safeValue(row["COMPLAINTER NAME"]),
          complainterPhone: safeValue(row["COMPLAINTER PHONE"]),
          complaintDate: toDDMMYYYY(row["COMPLAINT DATE"]),
          email: safeValue(row["EMAIL"]),
          phone: safeValue(row["PHONE"]),
          vpa: safeValue(row["VPA"]),
          amount: safeValue(row["AMOUNT"]),
          fees: safeValue(row["FEES"]),
        });
      });

      const formatted = Object.entries(grouped).map(([ackNo, utrs]) => ({
        ackNo,
        utrs,
      }));

      setAcks([]);

      setTimeout(() => {
        setAcks(formatted);
      }, 0);
      toast.success("Bulk import successful");
    };

    reader.readAsBinaryString(file);
  };

  const handleSubmit = async () => {
    try {
      for (const [ackIndex, ack] of acks.entries()) {
        if (!ack.ackNo?.trim()) {
          toast.error(`ACK number missing in ACK #${ackIndex + 1}`);
          return;
        }

        for (const [utrIndex, u] of ack.utrs.entries()) {
          if (!u.merchantId?.trim()) {
            toast.error(
              `Merchant missing in ACK #${ackIndex + 1}, UTR #${utrIndex + 1}`,
            );
            return;
          }
          if (!u.utr?.trim()) {
            toast.error(
              `UTR missing in ACK #${ackIndex + 1}, UTR #${utrIndex + 1}`,
            );
            return;
          }
          if (!u.orderId?.trim()) {
            toast.error(
              `Order ID missing in ACK #${ackIndex + 1}, UTR #${utrIndex + 1}`,
            );
            return;
          }
          if (!u.amount?.trim()) {
            toast.error(
              `Amount missing in ACK #${ackIndex + 1}, UTR #${utrIndex + 1}`,
            );
            return;
          }
        }

        const res = await createComplaint(
          {
            ackNo: ack.ackNo.trim(),
            utrs: ack.utrs.map((u) => ({
              orderId: String(u.orderId).trim(),
              utr: u.utr.trim(),
              merchantName: u.merchantId,
              customerName: u.customerName,
              complainterName: u.complainterName,
              complainterPhone: u.complainterPhone,
              complaintDate: u.complaintDate,
              email: u.email,
              phone: u.phone,
              vpa: u.vpa || "",
              amount: Number(u.amount),
              fees: Number(u.fees),
            })),
          },
          token,
        );

        const contentType = res?.headers?.["content-type"];

        if (
          contentType?.includes(
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          )
        ) {
          const blob = new Blob([res.data], { type: contentType });
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.href = url;
          a.download = `utr_not_found_ack_${ack.ackNo}.xlsx`;
          document.body.appendChild(a);
          a.click();
          a.remove();

          toast.warn(
            `ACK ${ack.ackNo}: Some UTRs not found. Excel downloaded.`,
          );
          continue;
        }

        const reader = new FileReader();
        reader.onload = () => {
          const json = JSON.parse(reader.result);

          if (json.created && json.updated) {
            toast.success(
              `ACK ${ack.ackNo}: ${json.created} created, ${json.updated} updated`,
            );
          } else if (json.created) {
            toast.success(
              `ACK ${ack.ackNo}: ${json.created} complaint(s) created`,
            );
          } else if (json.updated) {
            toast.info(
              `ACK ${ack.ackNo}: ${json.updated} complaint(s) updated`,
            );
          } else {
            toast.info(`ACK ${ack.ackNo}: No changes`);
          }
        };
        reader.readAsText(res.data);
      }

      setAcks([
        {
          ackNo: "",
          utrs: [{ ...emptyUtr }],
        },
      ]);
    } catch (err) {
      toast.error("Submission failed");
    }
  };

  return (
    <div className="complaint-container">
      <div className="complaint-header">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h1 className="complaint-title">Add Complaint</h1>
          <label htmlFor="file-upload" className="styled-button">
            Bulk Import
          </label>
          <input
            id="file-upload"
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={(e) => {
              handleExcelUpload(e.target.files[0]);
              e.target.value = null;
            }}
          />
        </div>
        <div style={{ display: "flex", gap: 12,marginBottom:12 }}>
          <h2>Acknowledgement</h2>
          <button className="add-btn" onClick={addAck}>
            + Add ACK
          </button>
        </div>
      </div>

      {acks.map((ack, ackIndex) => (
        <div key={ackIndex} className="ack-card">
          <div className="ack-header">
            <span className="ack-badge">ACK #{ackIndex + 1}</span>

            <input
              className="ack-input"
              placeholder="Acknowledgement Number"
              value={ack.ackNo}
              required
              onChange={(e) => updateAckNo(ackIndex, e.target.value)}
            />
          </div>

          {ack.utrs.map((item, utrIndex) => (
            <div className="utr-card" key={utrIndex}>
              <ReactSelect
                options={MerchantOptions}
                value={
                  item.merchantId
                    ? {
                        value: item.merchantId,
                        label: item.merchantName,
                      }
                    : null
                }
                onChange={(o) => {
                  updateUtr(ackIndex, utrIndex, "merchantId", o.value);
                  updateUtr(ackIndex, utrIndex, "merchantName", o.label);
                }}
                placeholder="Select Merchant"
              />

              <div className="input-with-btn">
                <input
                  className="input-strong"
                  placeholder="UTR"
                  value={item.utr}
                  required
                  onChange={(e) =>
                    updateUtr(
                      ackIndex,
                      utrIndex,
                      "utr",
                      e.target.value.replace(/\D/g, ""),
                    )
                  }
                />
              </div>

              <div className="utr-row-grid">
                <input
                  placeholder="Order ID"
                  required
                  value={item.orderId}
                  onChange={(e) =>
                    updateUtr(ackIndex, utrIndex, "orderId", e.target.value)
                  }
                />
                <input
                  placeholder="Customer Name"
                  value={item.customerName}
                  onChange={(e) =>
                    updateUtr(
                      ackIndex,
                      utrIndex,
                      "customerName",
                      e.target.value,
                    )
                  }
                />
                <input
                  placeholder="Email"
                  value={item.email}
                  onChange={(e) =>
                    updateUtr(ackIndex, utrIndex, "email", e.target.value)
                  }
                />
              </div>

              <div className="utr-row-grid">
                <input
                  placeholder="Phone"
                  value={item.phone}
                  onChange={(e) =>
                    updateUtr(ackIndex, utrIndex, "phone", e.target.value)
                  }
                />
                <input
                  placeholder="VPA"
                  value={item.vpa}
                  onChange={(e) =>
                    updateUtr(ackIndex, utrIndex, "vpa", e.target.value)
                  }
                />
                <input
                  placeholder="Amount"
                  value={item.amount}
                  onChange={(e) =>
                    updateUtr(
                      ackIndex,
                      utrIndex,
                      "amount",
                      e.target.value.replace(/\D/g, ""),
                    )
                  }
                />
              </div>

              <div className="utr-row-grid">
                <input
                  placeholder="Complainter Name"
                  value={item.complainterName}
                  onChange={(e) =>
                    updateUtr(
                      ackIndex,
                      utrIndex,
                      "complainterName",
                      e.target.value,
                    )
                  }
                />
                <input
                  placeholder="Complainter Phone"
                  value={item.complainterPhone}
                  onChange={(e) =>
                    updateUtr(
                      ackIndex,
                      utrIndex,
                      "complainterPhone",
                      e.target.value,
                    )
                  }
                />
                <input
                  type="date"
                  value={toYYYYMMDD(item.complaintDate)}
                  onChange={(e) =>
                    updateUtr(
                      ackIndex,
                      utrIndex,
                      "complaintDate",
                      toDDMMYYYY(e.target.value),
                    )
                  }
                />
              </div>
              <input
                placeholder="Fees"
                value={item.fees}
                onChange={(e) =>
                  updateUtr(
                    ackIndex,
                    utrIndex,
                    "fees",
                    e.target.value.replace(/\D/g, ""),
                  )
                }
              />

              {ack.utrs.length > 1 && (
                <button
                  className="remove-btn"
                  onClick={() => removeUtr(ackIndex, utrIndex)}
                >
                  Remove UTR
                </button>
              )}
            </div>
          ))}

          <button className="add-btn" onClick={() => addUtr(ackIndex)}>
            + Add UTR
          </button>
        </div>
      ))}

      <div className="btn-div">
        <button className="submit-btn" onClick={handleSubmit}>
          Submit for approval
        </button>
      </div>
    </div>
  );
}

export default ComplaintForm;
